import { useQuery } from '@tanstack/react-query';

import { reportingLeDataSessionPath } from '~/consts/reportingLeDataApi';

import { request } from '~/services/common';

import type { LeDataSessionResponse } from '~/types/leData';

import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';
import { parseLeDataSessionResponse } from '~/utils/mapLeDataSessionToMessages';
import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

export const reportingLeDataSessionQueryKeys = {
  all: ['reporting', 'le-data', 'session'] as const,
  detail: (sessionId: string) => [...reportingLeDataSessionQueryKeys.all, sessionId] as const,
};

export function useGetReportingLeDataSession(sessionId: string | null, enabled = true) {
  return useQuery<LeDataSessionResponse>({
    queryKey: reportingLeDataSessionQueryKeys.detail(sessionId ?? ''),
    queryFn: async ({ signal }) => {
      try {
        if (!sessionId) {
          throw new Error('Session ID is required');
        }
        const url = resolveReportingLeDataUrl(reportingLeDataSessionPath(sessionId));
        const data = await request(url, { signal });
        return parseLeDataSessionResponse(data);
      } catch (error) {
        throw new Error(getLeDataApiErrorMessage(error), { cause: error });
      }
    },
    enabled: Boolean(sessionId) && enabled,
    /** Opening a session should always re-fetch server history, even if we viewed it recently. */
    staleTime: 0,
  });
}
